import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowClockwise,
  ArrowRight,
  CheckCircle,
  FloppyDisk,
  Lock,
  PaperPlaneTilt,
} from '@phosphor-icons/react';
import { Alert, AlertDescription, AlertHeader } from '@clay-run/glaze/alert';
import { Badge, BadgeIcon } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import {
  CollapsibleCard,
  CollapsibleCardBody,
  CollapsibleCardHeader,
} from '@clay-run/glaze/collapsible-card';
import { Icon } from '@clay-run/glaze/icon';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { WriteRefusedError } from '../../../_shared/fetch-error';
import { useApi } from '../../../_shared/hooks';
import { useLauncherMembers } from '../../launcher-api';
import type { CampaignMember } from '../../../_shared/launcher-campaign.types';
import { getLibrary, getPlaybook, getPlays } from '../../api-client';
import { clonePlay, sendEnrollments } from '../../mutations';
import { isVersionConflictError } from '../../conflict-errors';
import { newestTestDispatchId, playSendsQueryKey } from '../../dispatch-inspector-logic';
import { libraryQueryKey, playbookDetailQueryKey, playsPrefixKey, playsQueryKey } from '../../query-keys';
import { DispatchInspector } from './DispatchInspector';
import { freshHeadToAdopt } from '../../playbook-draft';
import {
  adoptSavedPlaybookDraft,
  ensurePlaybookDraft,
  selectPlaybookDraft,
  usePlaybookDraftStore,
} from '../../playbook-draft-store';
import {
  reviewBucket,
  reviewGate,
  reviewMemberTally,
  rungKey,
  type ReviewableRung,
  type ReviewApprovals,
} from '../../review-gate';
import { useMatchedMembersLadder } from '../../use-matched-members';
import { channelLabel, notReadyBadgeText } from '../../sequence-readiness';
import { isArchivedInLibrary } from '../../play-ownership';
import { SEQUENCE_UNAVAILABLE_NAME } from '../../sequence-card-menu';
import { useRungReadiness } from '../../use-rung-readiness';
import { invalidatePlayLists, PLAYS_LIBRARY_QUERY_KEY } from '../../use-plays-library';
import {
  ALREADY_SENT_NOTE,
  isPlaySent,
  reviewActionsLocked,
  sendFailureInline,
  sendFailureOf,
  sendResultToast,
  tallyUnavailableSentence,
  testSendScopeSentence,
  type ReviewPendingAction,
} from '../../review-step-logic';
import { memberTestDisabledReason } from '../../member-test-logic';
import { openDispatchLog, openReview, setReviewOpen, setSendFailure, useReviewCardsStore } from '../../review-cards-store';
import { closeMemberTest, memberTestKey, openMemberResult, openMemberTest, useMemberTestStore } from '../../member-test-store';
import { MemberTestDrawer } from './MemberTestDrawer';
import { ReviewRungMembers } from './ReviewRungMembers';
import type {
  PlaybooksLibraryResponse,
  PlaybooksMatchedMembersRung,
  PlaybooksPlaybookDetailResponse,
  PlaybooksPlaySendsResponse,
} from '../../schemas';
import { launcherCampaignMembersQueryKey } from '../../../_shared/launcher-campaign-member-schema';

/**
 * Launcher V2 A2 U6 — the Review step (plan's `renderStep3`, v6 prototype
 * ~line 6665): a FLAT review-row list (no kebab — the prototype has none
 * here either), the member count plus the two bucket tallies, a per-row
 * approval control, and the Send/Save gate card. Zero version chrome: the
 * prototype's "@v" pins, branch badges and the "Saving creates <name> v3…"
 * sentence are all killed (plan R4) — saving just saves.
 *
 * Decision 12 / Decision 13: EXACTLY two buckets, "Routed" and
 * "No sequence". The prototype's third bucket ("Suppressed", plus
 * `kindBadge('suppression')`) is gone, and an unmapped rung lands in
 * "No sequence" with the same plain note the qualify and sequences steps use
 * — no suppression badge, no suppression vocabulary.
 *
 * ── SAVING IS GONE; ONLY "APPLY TO CAMPAIGNS" REMAINS (2026-09-02 rip-out,
 * U4): every ladder edit (add/remove rung, mapping, sender) now persists
 * write-through the moment the rep makes it, via
 * `playbook-draft-store.ts#persistLadder` — there is no more "staged, saved
 * later on Review" ladder to commit here. The plain "Save Playbook" button is
 * deleted along with the whole-ladder save/rebase choreography that used to
 * live in `handleSave`; that handler is gone. What survives is the "Apply to
 * campaigns" action (`handleApply` below), which as of plan 2026-09-27-006
 * (U7c, Key Technical Decision 7) calls the server-side `clonePlay` on THIS
 * play (`POST /plays/:id/clone`): a new play carrying this play's own
 * edits — its owned copies and rewritten ladder — rather than a fresh
 * snapshot of the template as of now. It never writes a ladder itself: the
 * clone endpoint copies this play's owned identities and rungs server-side.
 * Hidden for a play the machine API created. `reviewGate` (`review-gate.ts`)
 * still computes real disabled-state logic gated on every row being
 * approved — that gating is presentation-layer and untouched by this rip-out.
 * `mutations.sendEnrollments` is REAL as of PR D / U5b — a send attempt
 * POSTs the routed roster to the backend's `/plays/:id/send` and reports a
 * "Queued N enrollments" toast (an `'info'`-status toast naming how many
 * failed to start when `startFailed > 0` — Glaze's `ToastStatus` has no
 * `'warning'`). A failure — most commonly a 409 `version_conflict` when the
 * roster drifted since Review last matched it — surfaces the server's own
 * `detail` text via the thrown `FetchErrorLike`'s `.message`.
 *
 * ── REVIEW ROWS COME FROM THE DRAFT, NOT A SEPARATE STAGED COPY (PR C / U3C,
 * still true post-rip-out): a rung whose write-through "Add segmentation"
 * save has not yet resolved has no server id yet, so this step maps each
 * `DraftRung` to a `ReviewableRung` using `rung.id ?? rung.draftKey` as the
 * review/approvals key — the ONLY place a `draftKey` stands in for `id`, and
 * only for this presentation-layer purpose. One real consequence: while any
 * such in-flight rung is present, `reviewMemberTally`'s ladder join has no
 * entry for it and honestly reports "no number yet" (a dash) for the WHOLE
 * tally, exactly like every other staleness case it already guards against —
 * the ladder only ever describes PERSISTED state.
 *
 * ── PER-MEMBER ROUTING IS REAL AS OF PR B U3 (R5). A2's data gap ("the two
 * bucket tallies count SEGMENTATIONS, not members") is closed: one call to
 * U2's ladder endpoint resolves the whole first-match-wins pass, and
 * `reviewMemberTally` turns it into Routed / No-sequence MEMBER counts using
 * the same, unchanged `reviewBucket` classification the rows render. So:
 *   • the member count is the campaign's REAL roster size,
 *   • Routed / No-sequence are real member counts — a dash, never a fallback
 *     number, while the ladder is loading or has drifted from the rendered
 *     Playbook version,
 *   • `sendEnrollments` gets the routed member ids, not the whole roster,
 *   • the prototype's per-row member count and its literal "Send N
 *     enrollments" label are still NOT rendered — per-rung numbers on the
 *     review rows are presentation scope this unit didn't take on.
 *
 * ── GROUP FOLDERS (plan 2026-10-15-003, U4): the flat "Campaign members"
 * table that used to sit above this card is gone. Each group row is a Glaze
 * `CollapsibleCard` folder; opening it lists the people routed to that group
 * (`ReviewRungMembers`), and a final "Not in any group (N)" folder lists
 * everyone no group claimed, so every campaign member stays reachable from
 * here. The Preview button on a person opens their panel through `member-test-store`
 * (the drawer itself is a separate unit). Qualify and Sequences still mount
 * the shared `MembersPanel`; Review no longer does.
 */

/** `openFolderKey` of the "Not in any group" folder (a rung key can never collide: it has no spaces). */
const NOT_IN_ANY_GROUP_KEY = 'not in any group';


// One review row — a folder (plan 2026-10-15-003 U4)

/** "N people" for a folder header; a dash while the ladder is unusable (never a guessed number). */
function peopleLabel(count: number | null): string {
  if (count === null) return 'People: —';
  return `${count.toLocaleString('en-US')} ${count === 1 ? 'person' : 'people'}`;
}

/** Keeps a click on an inner control (Approve, a link) from also toggling the folder. */
function stopFolderToggle(event: MouseEvent): void {
  event.stopPropagation();
}

interface ReviewRungRowProps {
  rung: ReviewableRung;
  index: number;
  library: PlaybooksLibraryResponse | undefined;
  isApproved: boolean;
  /** Locked while a send, an apply or a reload is in flight (U10). */
  approvalDisabled: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** This group's ladder entry; `null` while the tally is unavailable. */
  ladderEntry: PlaybooksMatchedMembersRung | null;
  /** The shared "ladder unusable" sentence, or `null` when the ladder is usable. */
  unavailableSentence: string | null;
  members: readonly CampaignMember[];
  membersLoading: boolean;
  selectedMemberId: string | null;
  onOpenMember: (memberId: string) => void;
  /** `undefined` in Playbook mode: rows carry no Test button. */
  playId: string | undefined;
  /** `tally.routedMemberIds`; `null` while the ladder is unavailable. */
  routedMemberIds: string[] | null;
  actionsLocked: boolean;
  onToggleApproval: () => void;
  onGoToConditions: () => void;
  onGoToSequence: () => void;
}

function ReviewRungRow({
  rung,
  index,
  library,
  isApproved,
  approvalDisabled,
  open,
  onOpenChange,
  ladderEntry,
  unavailableSentence,
  members,
  membersLoading,
  selectedMemberId,
  onOpenMember,
  playId,
  routedMemberIds,
  actionsLocked,
  onToggleApproval,
  onGoToConditions,
  onGoToSequence,
}: ReviewRungRowProps) {
  const segmentation = library?.segmentations.find((s) => s.id === rung.segmentationId);
  // Plan 2026-09-28-006 U4 ([R20]) — never the raw id when the row is absent.
  const segmentationName = segmentation?.name ?? 'Segment unavailable';
  const sequence = rung.mapping
    ? library?.sequences.find((s) => s.id === rung.mapping!.sequenceId)
    : undefined;
  const bucket = reviewBucket(rung);
  // R6 — v1-parity Ready badge (PR C, U5). `rung.mapping` here already comes
  // from the STAGED draft (`ReviewStep`'s `reviewRungs`, per this file's own
  // header comment), so a sender just set in `SequencesStep`'s modal shows up
  // here without waiting for a save.
  const { readiness, reasons } = useRungReadiness(rung.mapping, library);
  // One reason per group, shared by every row's Test button (the panel computes the same inputs).
  const testDisabledReason = memberTestDisabledReason({
    hasPlay: playId !== undefined,
    inGroup: true,
    rungMapped: rung.mapping != null,
    notReadyText: readiness === 'not_ready' && rung.mapping ? notReadyBadgeText(reasons, rung.mapping.channel) : null,
    tallyAvailable: routedMemberIds !== null,
    actionsLocked,
  });

  return (
    <section
      data-review-folder="true"
      aria-label={`Group ${index + 1}: ${segmentationName}`}
      className="border-b border-border-secondary last:border-b-0"
    >
      <CollapsibleCard open={open} onOpenChange={onOpenChange} className="rounded-none border-0">
        <CollapsibleCardHeader className="!items-center py-3">
          {/* The whole header row opens the folder; every control inside it stops the click. */}
          <div
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3"
            onClick={() => onOpenChange(!open)}
          >
            <Text size="xs" variant="tertiary" className="w-4 shrink-0 text-right tabular-nums">
              {index + 1}
            </Text>

            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span onClick={stopFolderToggle}>
                  <Button variant="inline-text" size="xs" onClick={onGoToConditions}>
                    {segmentationName}
                  </Button>
                </span>
                {rung.isCatchAll && (
                  <Badge color="purple">
                    <BadgeIcon type={Lock} />
                    Catch-all
                  </Badge>
                )}
                <Text size="xs" variant="tertiary" className="tabular-nums">
                  {peopleLabel(ladderEntry?.totalMatched ?? null)}
                </Text>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {rung.mapping ? (
                  <>
                    <span onClick={stopFolderToggle}>
                      <Button variant="inline-text" size="xs" onClick={onGoToSequence}>
                        {sequence?.name ?? SEQUENCE_UNAVAILABLE_NAME}
                        <Icon type={ArrowRight} />
                      </Button>
                    </span>
                    {/* Plan 2026-09-28-006 U4 ([R18]) — a Library piece archived while this play still uses it. */}
                    {sequence && isArchivedInLibrary(sequence) && (
                      <StatusBadge status="neutral">Archived in Library</StatusBadge>
                    )}
                    <Badge>{channelLabel(rung.mapping.channel)}</Badge>
                    {readiness === 'ready' && <StatusBadge status="success">Ready</StatusBadge>}
                    {readiness === 'not_ready' && (
                      <StatusBadge status="warning">{notReadyBadgeText(reasons, rung.mapping.channel)}</StatusBadge>
                    )}
                  </>
                ) : (
                  <Text size="xs" variant="tertiary">
                    No sequence mapped — members here don&apos;t get a send.
                  </Text>
                )}
              </div>
            </div>

            <div className="w-24 shrink-0 text-right">
              <Text size="xs" variant="tertiary">
                {bucket}
              </Text>
            </div>

            <div className="shrink-0" onClick={stopFolderToggle}>
              {isApproved ? (
                <Button size="sm" onClick={onToggleApproval} title="Unapprove" disabled={approvalDisabled}>
                  <Icon type={CheckCircle} />
                  Approved
                </Button>
              ) : (
                <Button variant="secondary" size="sm" onClick={onToggleApproval} disabled={approvalDisabled}>
                  Approve
                </Button>
              )}
            </div>
          </div>
        </CollapsibleCardHeader>
        <CollapsibleCardBody className="border-border-secondary p-0">
          <ReviewRungMembers
            source={ladderEntry}
            members={members}
            unavailableSentence={unavailableSentence}
            membersLoading={membersLoading}
            emptyText="No one routes to this group."
            selectedMemberId={selectedMemberId}
            onOpenMember={onOpenMember}
            playId={playId}
            routedMemberIds={routedMemberIds}
            testDisabledReason={testDisabledReason}
            groupKey={rungKey(rung)}
          />
        </CollapsibleCardBody>
      </CollapsibleCard>
    </section>
  );
}

/** The final, non-approvable folder: members no group claimed (nothing routes them, so Test is greyed with the reason). */
function NotInAnyGroupFolder({
  excludedMemberIds,
  open,
  onOpenChange,
  members,
  membersLoading,
  selectedMemberId,
  onOpenMember,
  playId,
  routedMemberIds,
  actionsLocked,
}: {
  excludedMemberIds: readonly string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  members: readonly CampaignMember[];
  membersLoading: boolean;
  selectedMemberId: string | null;
  onOpenMember: (memberId: string) => void;
  playId: string | undefined;
  routedMemberIds: string[] | null;
  actionsLocked: boolean;
}) {
  const testDisabledReason = memberTestDisabledReason({
    hasPlay: playId !== undefined,
    inGroup: false,
    rungMapped: false,
    notReadyText: null,
    tallyAvailable: routedMemberIds !== null,
    actionsLocked,
  });
  return (
    <section
      data-review-folder="true"
      aria-label="Not in any group"
      className="border-b border-border-secondary last:border-b-0"
    >
      <CollapsibleCard open={open} onOpenChange={onOpenChange} className="rounded-none border-0">
        <CollapsibleCardHeader className="!items-center py-3">
          <div
            className="flex min-w-0 flex-1 cursor-pointer flex-col gap-1"
            onClick={() => onOpenChange(!open)}
          >
            <Text size="sm" weight="medium">
              {`Not in any group (${excludedMemberIds.length.toLocaleString('en-US')})`}
            </Text>
            <Text size="xs" variant="tertiary">
              This Playbook has no catch-all, so nothing routes these members.
            </Text>
          </div>
        </CollapsibleCardHeader>
        <CollapsibleCardBody className="border-border-secondary p-0">
          <ReviewRungMembers
            source={excludedMemberIds}
            members={members}
            unavailableSentence={null}
            membersLoading={membersLoading}
            emptyText="No one is left out."
            selectedMemberId={selectedMemberId}
            onOpenMember={onOpenMember}
            playId={playId}
            routedMemberIds={routedMemberIds}
            testDisabledReason={testDisabledReason}
          />
        </CollapsibleCardBody>
      </CollapsibleCard>
    </section>
  );
}

// Step

export interface ReviewStepProps {
  campaignSfdcId: string;
  /**
   * Optional as of terra-qyt.14 (U9) — `undefined` in playbook mode, where
   * `playbookId` below is the real identity, no `getPlays` lookup runs, and
   * the Send/Apply gate card is replaced by a plain note (see the render
   * below): a playbook has no Play to send or apply from.
   */
  playId?: string;
  /**
   * terra-qyt.14 (U9) — playbook mode's identity, supplied directly by
   * `PlayFlowShell` — see `QualifyStepProps.playbookId`'s identical doc.
   */
  playbookId?: string;
  onGoToQualify: () => void;
  onGoToSequences: () => void;
}

export function ReviewStep({
  campaignSfdcId,
  playId,
  playbookId,
  onGoToQualify,
  onGoToSequences,
}: ReviewStepProps) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const { members, isLoading: isMembersLoading } = useLauncherMembers(campaignSfdcId);
  const [approvals, setApprovals] = useState<ReviewApprovals>({});
  const [pendingAction, setPendingAction] = useState<ReviewPendingAction>(null);
  const actionsLocked = reviewActionsLocked(pendingAction);
  const [isRechecking, setIsRechecking] = useState(false);
  // The Review-and-send card's open/closed choice (collapsed on arrival) — a store, so an SSE re-render cannot reset it.
  const reviewOpen = useReviewCardsStore((state) => state.reviewOpen);
  /**
   * The ONE open group folder (an accordion), keyed by `rungKey(rung)` or
   * {@link NOT_IN_ANY_GROUP_KEY}; `null` = all closed (the default). Opening a
   * folder closes the others. Plain view state: resets on a tab switch — the
   * Dispatch log's cards behave the same.
   */
  const [openFolderKey, setOpenFolderKey] = useState<string | null>(null);
  const openMemberId = useMemberTestStore((state) => state.openMemberId);
  const openRungId = useMemberTestStore((state) => state.openRungId);
  // A panel opened on one exact Dispatch-log row (a name in "All people") — it
  // must open for anyone on the roster, even a person no group holds.
  const openHasFocus = useMemberTestStore(
    (state) =>
      playId !== undefined &&
      state.openMemberId !== null &&
      state.focusByMember[memberTestKey(playId, state.openMemberId)] !== undefined,
  );
  function setFolderOpen(key: string, open: boolean) {
    setOpenFolderKey((current) => (open ? key : current === key ? null : current));
  }
  /**
   * U7b — the Test send / Real send toggle (plan `2026-09-15-001`, "U7.",
   * Key Technical Decision 6). Plain component `useState`, not a
   * `useDraftState`: this value is never seeded from server data (there is
   * no server-side "current send mode" to reseed from on an SSE
   * `artifact:updated`), so the draft-state-under-SSE pattern doesn't apply
   * — see `docs/v4-system/draft-state-under-sse.md`'s own scoping of
   * `useDraftState` to state seeded from a fetched/pushed value.
   *
   * Defaults to `'test'` (never `'real'`): the packet's hard requirement is
   * that a rep must opt IN to a real send, never the reverse — a real send
   * enrolls people in Gong and cannot be undone, while a test send is
   * side-effect-free and repeatable (R16).
   */
  const [sendMode, setSendMode] = useState<'test' | 'real'>('test');
  /**
   * U8, Codex C1 — the mode a SUBMITTED send is running under, captured at
   * click time. Plain `useState`, same reasoning as `sendMode` above: pure
   * view state, never seeded from server data. Exists because `sendMode`
   * itself is live — a rep can flip the toggle while a request is still in
   * flight — and the pending label/helper below must describe the request
   * that was actually SENT, not whatever the toggle currently shows. `null`
   * whenever no send is in flight.
   */
  const [pendingSendMode, setPendingSendMode] = useState<'test' | 'real' | null>(null);
  // U6b — the dispatch inspector auto-opens the just-created send's card;
  // see `DispatchInspector`'s own header comment ("OPEN-ON-SEND") for why a
  // plain `useState` (not a ref) is the right home for this.
  const [focusSendId, setFocusSendId] = useState<string | null>(null);
  // U0a — a TEST send has no send row to open by; its card opens by dispatch id.
  const [focusTestDispatchId, setFocusTestDispatchId] = useState<string | null>(null);
  /**
   * U0g — set when a send was refused because the recipients or groups changed
   * after the rep reviewed. Plain UI state (never seeded from server data).
   * `reloaded` flips once the rep has pressed "Reload and review again".
   */
  // The persistent "that send did not start" message lives in `review-cards-store` (a folder's "Test selected" writes it too).
  const storedSendFailure = useReviewCardsStore((state) => state.sendFailure);
  const lastSendFailure = storedSendFailure !== null && storedSendFailure.playId === playId ? storedSendFailure : null;
  // The play this step is showing NOW (null once unmounted) — reset at the top of the effect so StrictMode's setup→cleanup→setup leaves it correct.
  const livePlayId = useRef<string | null>(playId ?? null);
  useEffect(() => {
    livePlayId.current = playId ?? null;
    return () => {
      livePlayId.current = null;
    };
  }, [playId]);
  const [rosterChange, setRosterChange] = useState<{ message: string; reloaded: boolean } | null>(null);

  const libraryQuery = useQuery({
    queryKey: libraryQueryKey(playId),
    queryFn: () => getLibrary(apiFetch, { playId }),
  });

  // terra-qyt.14 (U9) — SKIPPED (not merely disabled) in playbook mode; see
  // `QualifyStep`'s identical query for the full doc.
  const playsQuery = useQuery({
    queryKey: playsQueryKey(campaignSfdcId),
    queryFn: () => getPlays(apiFetch, { campaignSfdcId }),
    enabled: !!campaignSfdcId && playbookId === undefined,
  });

  const play = playsQuery.data?.plays.find((p) => p.id === playId) ?? null;
  // terra-qyt.14 (U9) — one expression, not a new hook (see `QualifyStep`).
  const resolvedPlaybookId = playbookId ?? play?.playbookId;

  const playbookQuery = useQuery({
    queryKey: playbookDetailQueryKey(resolvedPlaybookId ?? '', playId),
    queryFn: () => getPlaybook(apiFetch, resolvedPlaybookId!, { playId }),
    enabled: !!resolvedPlaybookId,
  });

  /**
   * The whole first-match-wins pass, ONE call for the whole Playbook (U3/R5).
   * Same query key `MembersPanel` uses, so the panel's per-rung preview and
   * these tallies are the same fetch and can never disagree.
   */
  const ladderQuery = useMatchedMembersLadder(campaignSfdcId, resolvedPlaybookId ?? null, playId);

  /**
   * The staged playbook draft (PR C, U3C — see this file's header). Seeded
   * here too, not only in `QualifyStep`: Glaze `Tabs` unmounts inactive
   * panels, so a rep who navigates straight to `?step=review` never mounts
   * `QualifyStep` at all. `ensurePlaybookDraft` is idempotent per playbook id,
   * so seeding from both steps can never race or double-seed.
   */
  const draft = usePlaybookDraftStore(selectPlaybookDraft);
  useEffect(() => {
    // `playbookQuery.data !== undefined` (not just `resolvedPlaybookId`) is
    // the real gate — see `SequencesStep`'s identical effect for the full
    // race-condition writeup (Codex-hardening fix found while building U5):
    // seeding with a premature `?? null` head permanently strands this
    // playbookId on an empty draft once the real head arrives a tick later.
    if (resolvedPlaybookId && playbookQuery.data !== undefined) {
      // Plan 002, U6 (Decision 16) — in a play the draft writes the play's ladder.
      ensurePlaybookDraft(resolvedPlaybookId, playbookQuery.data.head, playId ?? null);
    }
  }, [resolvedPlaybookId, playbookQuery.data, playId]);

  /**
   * The live draft ladder — see this file's header for why it's the draft
   * and not a separate staged copy. A rung with no server id yet (still
   * mid-flight on its own write-through save) is addressed by its
   * `draftKey` for review/approval purposes only.
   */
  const reviewRungs: ReviewableRung[] = (draft?.rungs ?? []).map((rung) => ({
    id: rung.id ?? rung.draftKey,
    segmentationId: rung.segmentationId,
    mapping: rung.mapping,
    isCatchAll: rung.isCatchAll,
  }));
  const gate = reviewGate(reviewRungs, approvals);
  /**
   * Routed / No-sequence as MEMBER counts. `null` while the ladder is loading,
   * failed, describes a different playbook VERSION than the one rendered
   * (Codex round-2 finding #3 — a reorder can permute the same rung-key set
   * without the key set itself changing, so `draft?.baseVersionId` is
   * threaded through explicitly rather than trusting the key-set check
   * alone), or describes a different set of rungs than the ones rendered
   * (including any rung still only staged, not yet saved) — those render as
   * a dash, never as a zero that would under-report a send.
   */
  const tally = reviewMemberTally(reviewRungs, ladderQuery.data, draft?.baseVersionId ?? null);
  /** One sentence for "the ladder is unusable", shared by the header and every folder body. */
  const unavailableSentence = tally
    ? null
    : tallyUnavailableSentence({ isError: ladderQuery.isError, isPending: ladderQuery.isPending });

  /**
   * The one-person test panel (plan 2026-10-15-003 U5): derived at render from
   * the store's open person. If that person is no longer in the group's rows
   * (or the roster) the panel is simply not rendered — nothing here writes the
   * store. `null` for "Not in any group" is `openRungId === null`.
   */
  const openMember = openMemberId === null ? undefined : members.find((m) => m.id === openMemberId);
  const openLadder = ladderQuery.data;
  // A row-focused panel was opened from a table that may be stale, so its group
  // is re-derived from the ladder now: the group holding the person that the
  // saved Playbook still has, else none.
  const effectiveRungId =
    openHasFocus && openLadder !== undefined && openMember !== undefined
      ? (openLadder.rungs.find(
          (entry) => entry.matchedMemberIds.includes(openMember.id) && reviewRungs.some((rung) => rungKey(rung) === entry.rungId),
        )?.rungId ?? null)
      : openRungId;
  const openRungIndex =
    effectiveRungId === null ? -1 : reviewRungs.findIndex((rung) => rungKey(rung) === effectiveRungId);
  const openInGroupRows =
    openMember !== undefined &&
    (effectiveRungId === null
      ? openLadder === undefined || openLadder.excludedMemberIds.includes(openMember.id)
      : openRungIndex >= 0 &&
        (openLadder === undefined ||
          openLadder.rungs.find((entry) => entry.rungId === effectiveRungId)?.matchedMemberIds.includes(openMember.id) ===
            true));
  // A row-focused panel also opens for a roster person who is in no CURRENT
  // group (not excluded, or their group is gone from the saved Playbook): it
  // shows as "not in any group", which greys Test with its own sentence.
  const openInRows = openInGroupRows || (openHasFocus && openMember !== undefined);

  /**
   * "Re-check" is a real READ refresh (the prototype's roster re-check), not a
   * write: it invalidates this step's `/v2` reads plus the launcher members
   * read `MembersPanel` shares. The members key is spelled out here because
   * `useLauncherMembers` exposes no refetch of its own.
   */
  async function handleRecheck() {
    setIsRechecking(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['playbooks'] }),
        queryClient.invalidateQueries({ queryKey: launcherCampaignMembersQueryKey(campaignSfdcId) }),
      ]);
    } finally {
      setIsRechecking(false);
    }
  }

  /**
   * The real send (PR D / U5b). `tally.routedMemberIds` is the routed subset
   * the rep reviewed (see the button's own comment below); the server
   * re-derives the ladder and fails closed with 409 `version_conflict` if it
   * disagrees, so a stale roster surfaces as a real error here, not a silent
   * wrong send.
   *
   * U7b: `sendMode` decides `isTest` — the only place in this component that
   * reads the toggle. A test send resolves every step exactly like a real
   * one (server-side, U3/U4) and is spammable; nothing here special-cases it
   * beyond the flag and the toast/label copy below.
   */
  async function handleSend() {
    if (!playId || !tally) return;
    const clickedPlayId = playId;
    const isTest = sendMode === 'test';
    // U8, Codex C1: capture the mode BEFORE it can change under this request
    // — `sendMode` is live and the toggle stays interactive-in-principle
    // until `pendingAction` below takes effect on the next render.
    setPendingSendMode(sendMode);
    setPendingAction('send');
    try {
      setRosterChange(null);
      setSendFailure(null);
      const response = await sendEnrollments(apiFetch, playId, tally.routedMemberIds, isTest);
      await queryClient.invalidateQueries({ queryKey: playsPrefixKey() });
      // U1 (2026-09-17) — a send flips `status` (draft -> launched); the
      // Library's "Plays" section reads that status too, so it needs its own
      // invalidation alongside the campaign-scoped prefix above.
      await queryClient.invalidateQueries({ queryKey: PLAYS_LIBRARY_QUERY_KEY });
      // U6b: invalidate the sends query (shared key with `DispatchInspector`'s
      // own fetch, `dispatch-inspector-logic.ts#playSendsQueryKey`) and open
      // the card of the send this just created: a real send by its send id, a
      // test (which has its own send row now, but is listed under "Test
      // sends") by its dispatch id (U0a).
      await queryClient.invalidateQueries({ queryKey: playSendsQueryKey(playId) });
      // The log starts collapsed; a fresh send expands it so the rep sees progress.
      openDispatchLog();
      if (response.isTest) setFocusTestDispatchId(response.dispatchId);
      else setFocusSendId(response.sendId);
      addToast(sendResultToast(response, isTest));
    } catch (error) {
      // core `apiFetch` already told the rep about a refused write (read-only
      // impersonation) — never toast it twice.
      if (!(error instanceof WriteRefusedError)) {
        // Whatever went wrong, the SERVER may have accepted the send (the
        // browser's own timeout is shorter than any server work used to be):
        // look at the true state before the rep reads a word of this — a stale
        // "draft" with a lost send is exactly how a rep pressed Send twice.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: playsPrefixKey() }),
          queryClient.invalidateQueries({ queryKey: PLAYS_LIBRARY_QUERY_KEY }),
          queryClient.invalidateQueries({ queryKey: playSendsQueryKey(playId) }),
        ]);
        const failure = sendFailureOf(error);
        if (failure.status === 'info') addToast({ content: failure.content, status: 'info' });
        else addErrorToast({ content: failure.content });
        // A test is already running: take the rep to it.
        if (failure.testInFlight) {
          const newest = newestTestDispatchId(
            queryClient.getQueryData<PlaybooksPlaySendsResponse>(playSendsQueryKey(playId)),
          );
          if (newest !== null) {
            openDispatchLog();
            setFocusTestDispatchId(newest);
          }
        }
        if (failure.rosterChanged) setRosterChange({ message: failure.content, reloaded: false });
        else {
          // Stays on screen (the toast does not). The send controls live inside the
          // Review and send card, which starts collapsed — open it so the rep sees why.
          const mode = isTest ? 'test' : 'real';
          setSendFailure({ ...sendFailureInline(failure, mode, new Date()), playId: clickedPlayId });
          // A failure that lands after the rep moved to another play is kept under THAT play; it must not open this one's card.
          if (livePlayId.current === clickedPlayId) openReview();
        }
      }
    } finally {
      setPendingAction(null);
      setPendingSendMode(null);
    }
  }

  /**
   * After a re-read of the play-scoped detail: adopt its head into the draft
   * when it really moved and nothing is mid-flight (`freshHeadToAdopt`, the
   * one rule). The rows come from the draft, which `ensurePlaybookDraft` never
   * reseeds — without this, a changed ladder leaves the review tally `null`
   * and Send disabled.
   */
  function adoptFreshPlaybookHead(): void {
    if (!resolvedPlaybookId) return;
    const fresh = queryClient.getQueryData<PlaybooksPlaybookDetailResponse>(
      playbookDetailQueryKey(resolvedPlaybookId, playId),
    );
    const head = freshHeadToAdopt(fresh?.head, usePlaybookDraftStore.getState().draft);
    if (head !== null) adoptSavedPlaybookDraft(resolvedPlaybookId, head, playId ?? null);
  }

  /**
   * U0g — "Reload and review again": re-read the roster and the groups' member
   * lists (the same refresh as Re-check), adopt the ladder as it is NOW if it
   * changed (review-fix U11, C6-2), and clear every approval so the rep
   * approves the groups as they are now. It never sends anything. Locked
   * throughout (second review-fix pass U10, C6-1): approvals are keyed by
   * rung, so one given to the OLD groups mid-reload would survive onto the new
   * roster — they are cleared again once the fresh roster is on screen.
   */
  async function handleReloadAndReview() {
    setPendingAction('reload');
    try {
      setApprovals({});
      await handleRecheck();
      adoptFreshPlaybookHead();
      setApprovals({});
      setRosterChange((prev) => (prev === null ? null : { ...prev, reloaded: true }));
    } finally {
      setPendingAction(null);
    }
  }

  /**
   * "Apply to campaigns" — as of plan 2026-09-27-006 (U7c, Key Technical
   * Decision 7): clones THIS play server-side (`clonePlay`,
   * `POST /plays/:id/clone`) into a new play on the same campaign, carrying
   * this play's own owned copies and rewritten rungs forward — not a fresh
   * snapshot of the template as of now. The client never posts a ladder: the
   * clone endpoint copies this play's owned identities and rungs itself. The
   * user-visible outcome (one new play for the campaign, the same success
   * toast) is unchanged. The button is hidden for a play the machine API
   * created (`play.machineCreatedAt !== null`).
   *
   * Plan 2026-09-27-007 U2 (Key Decision 6) — the clone is CAS'd on the ladder
   * the rep REVIEWED: `expectedVersionId` is the play-scoped head this step
   * rendered (`playbookQuery`, which every write-through ladder save patches
   * via `applyLadderSaveToCache`). If another tab or the AI changed the play
   * since, the server answers 409 `version_conflict` ("This play changed since
   * you reviewed it…"): the toast shows that sentence verbatim, approvals are
   * cleared, the play-scoped detail is refetched and adopted into the draft
   * (the rows come from the draft, which `ensurePlaybookDraft` never reseeds)
   * so the rep reviews — and approves — the fresh ladder before Apply is
   * offered again. The adopt is skipped while a rung is still mid-flight on
   * its own write-through save (no server id yet): that save's own 409
   * rebase re-anchors the draft, and `adoptSavedPlaybookDraft`'s index-based
   * copy-draft migration only holds for a response to THIS draft's own save.
   */
  async function handleApply() {
    const reviewedVersionId = playbookQuery.data?.head?.versionId;
    if (!play || reviewedVersionId === undefined) return;
    setPendingAction('apply');
    try {
      await clonePlay(apiFetch, play.id, { campaignSfdcId, expectedVersionId: reviewedVersionId });
      setApprovals({});
      await invalidatePlayLists(queryClient, campaignSfdcId);
      addToast({ content: 'Applied — 1 Play created.', status: 'success' });
    } catch (error) {
      addErrorToast({
        content: error instanceof Error && error.message ? error.message : 'Could not apply to campaigns — try again.',
      });
      if (isVersionConflictError(error) && resolvedPlaybookId) {
        setApprovals({});
        await queryClient.invalidateQueries({ queryKey: playbookDetailQueryKey(resolvedPlaybookId, playId) });
        adoptFreshPlaybookHead();
      }
    } finally {
      setPendingAction(null);
    }
  }

  // terra-qyt.14 (U9) — play-mode-only guards; see `SequencesStep`'s
  // identical structure for why playbook mode never reaches these.
  if (playbookId === undefined) {
    if (playsQuery.isLoading) {
      return (
        <div className="flex justify-center p-6">
          <LoadingSpinner size="md" />
        </div>
      );
    }

    if (playsQuery.isError) {
      return (
        <div className="p-6">
          <Alert status="error">Couldn&apos;t load this play. Please try again.</Alert>
        </div>
      );
    }

    if (!play) {
      return (
        <div className="p-6">
          <Text size="sm" variant="tertiary">
            This Play wasn&apos;t found for this campaign — open it again from the campaign home page.
          </Text>
        </div>
      );
    }
  }

  if (playbookQuery.isLoading) {
    return (
      <div className="flex justify-center p-6">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (playbookQuery.isError) {
    return (
      <div className="p-6">
        <Alert status="error">Couldn&apos;t load this Play&apos;s Playbook. Please try again.</Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      {openMember !== undefined && openInRows && (
        <MemberTestDrawer
          // A new person (or play) gets a fresh panel: no failure sentence follows the rep from one person to the next.
          key={`${playId ?? ''}:${openMember.id}`}
          playId={playId}
          member={openMember}
          rung={openInGroupRows && effectiveRungId !== null ? (reviewRungs[openRungIndex] ?? null) : null}
          groupNumber={openInGroupRows && openRungIndex >= 0 ? openRungIndex + 1 : null}
          library={libraryQuery.data}
          routedMemberIds={tally?.routedMemberIds ?? null}
          actionsLocked={actionsLocked}
          onClose={closeMemberTest}
        />
      )}
      {/* ── Review and send card ─────────────────────────────────────────────────── */}
      <div data-review-card="Review and send">
      <CollapsibleCard
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        className="border-border-primary bg-bg-primary"
      >
        <CollapsibleCardHeader className="!items-center">
          {/* The whole header row toggles the card; the buttons inside it stop the click. */}
          <div
            className="flex min-w-0 flex-1 cursor-pointer flex-wrap items-center justify-between gap-2"
            onClick={() => setReviewOpen(!reviewOpen)}
          >
            <div className="flex min-w-0 flex-col gap-1">
              <Text size="sm" weight="semibold">
                Review and send
              </Text>
          <div className="flex flex-wrap items-baseline gap-2">
            <Text size="sm" weight="medium" className="tabular-nums">
              {isMembersLoading ? '—' : members.length}
            </Text>
            <Text size="sm" variant="tertiary">
              members
            </Text>
            <Text size="sm" variant="placeholder">
              ·
            </Text>
            <Text size="sm" weight="medium" className="tabular-nums">
              {tally ? tally.routedCount : '—'}
            </Text>
            <Text size="sm" variant="tertiary">
              routed
            </Text>
            <Text size="sm" variant="placeholder">
              ·
            </Text>
            <Text size="sm" weight="medium" className="tabular-nums">
              {tally ? tally.noSequenceCount : '—'}
            </Text>
            <Text size="sm" variant="tertiary">
              no sequence
            </Text>
          </div>
          {/*
            U3/R5: these are real MEMBER counts now — first-match-wins across
            the ladder, with an unmapped rung's members counted as no-sequence
            (Decision 13: an unmapped rung is a real send exclusion, not just
            copy). Until the ladder answers there is no honest number, so the
            counts show a dash and say why rather than falling back to the old
            segmentation counts.
          */}
          {!tally && (
            <Text size="xs" variant={ladderQuery.isError ? 'critical' : 'tertiary'}>
              {unavailableSentence}
            </Text>
          )}
          {gate.totalCount > 0 && (
            <Text size="xs" variant="tertiary">
              {`${gate.approvedCount} of ${gate.totalCount} groups approved · ${
                playId === undefined ? 'Playbook saves as you edit' : gate.isSendEnabled && tally !== null ? 'Ready to send' : 'Not ready to send'
              }`}
            </Text>
          )}
            </div>
            <div className="flex shrink-0 items-center gap-2" onClick={stopFolderToggle}>
              {!gate.allApproved && gate.totalCount > 0 && (
                <Button
                  size="sm"
                  disabled={actionsLocked}
                  onClick={() =>
                    setApprovals(Object.fromEntries(reviewRungs.map((rung) => [rungKey(rung), true])))
                  }
                >
                  Approve all groups
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                disabled={isRechecking}
                onClick={() => void handleRecheck()}
              >
                <Icon type={ArrowClockwise} />
                {isRechecking ? 'Re-checking…' : 'Re-check'}
              </Button>
            </div>
          </div>
        </CollapsibleCardHeader>
        <CollapsibleCardBody className="border-border-secondary p-0">
        {gate.totalCount === 0 ? (
          <div className="p-4">
            <Text size="sm" variant="tertiary">
              No segmentations on this Playbook yet — add one on the Qualify step.
            </Text>
          </div>
        ) : (
          <div>
            {reviewRungs.map((rung, i) => (
              <ReviewRungRow
                key={rung.id}
                rung={rung}
                index={i}
                library={libraryQuery.data}
                isApproved={approvals[rungKey(rung)] === true}
                approvalDisabled={actionsLocked}
                open={openFolderKey === rungKey(rung)}
                onOpenChange={(open) => setFolderOpen(rungKey(rung), open)}
                ladderEntry={
                  tally ? ladderQuery.data?.rungs.find((entry) => entry.rungId === rungKey(rung)) ?? null : null
                }
                unavailableSentence={unavailableSentence}
                members={members}
                membersLoading={isMembersLoading}
                selectedMemberId={openMemberId}
                onOpenMember={(memberId) => openMemberTest(memberId, rungKey(rung))}
                playId={playId}
                routedMemberIds={tally?.routedMemberIds ?? null}
                actionsLocked={actionsLocked}
                onToggleApproval={() =>
                  setApprovals((prev) => ({
                    ...prev,
                    [rungKey(rung)]: !prev[rungKey(rung)],
                  }))
                }
                onGoToConditions={onGoToQualify}
                onGoToSequence={onGoToSequences}
              />
            ))}
            {tally && ladderQuery.data && ladderQuery.data.excludedMemberIds.length > 0 && (
              <NotInAnyGroupFolder
                excludedMemberIds={ladderQuery.data.excludedMemberIds}
                open={openFolderKey === NOT_IN_ANY_GROUP_KEY}
                onOpenChange={(open) => setFolderOpen(NOT_IN_ANY_GROUP_KEY, open)}
                members={members}
                membersLoading={isMembersLoading}
                selectedMemberId={openMemberId}
                onOpenMember={(memberId) => openMemberTest(memberId, null)}
                playId={playId}
                routedMemberIds={tally.routedMemberIds}
                actionsLocked={actionsLocked}
              />
            )}
          </div>
        )}
{/*
          terra-qyt.14 (U9) — playbook mode has no Play to send from or apply
          to (`handleApply` itself already no-ops on `!play`; this is what
          keeps the rep from ever reaching it). Review rows and approvals above
          are unaffected — they read the draft, which exists in both modes.
        */}
        {playId ? (
      <div className="flex flex-col gap-3 border-t border-border-secondary p-4">
        {!gate.anyRouted && (
          <Text size="xs" variant="tertiary">
            No segmentation is mapped to a sequence yet.
          </Text>
        )}
        {/*
          U7b — Test send / Real send toggle (plan `2026-09-15-001`,
          "U7."; `SegmentedControl` API copied from
          `SequencesStep.tsx`'s Edit | Preview toggle rather than
          guessed). Defaults to `'test'` via the `sendMode` state above
          — a rep must opt IN to `'real'`, never the reverse (the state is
          component-local, so every fresh mount starts on Test send).
        */}
          {/*
            U8, Codex C1 — locked for the duration of a send
            (`pendingAction !== null`, real OR test): a rep who flips
            Test↔Real while a request is in flight cannot be shown a
            mode different from the one that was actually submitted.
            The captured `pendingSendMode` (not the live `sendMode`)
            drives the send button's label while a send is pending.
          */}
          <SegmentedControl
            size="sm"
            value={sendMode}
            onValueChange={setSendMode}
            disabled={pendingAction !== null}
          >
            <SegmentedControlOption value="test">Test send</SegmentedControlOption>
            <SegmentedControlOption value="real">Real send</SegmentedControlOption>
          </SegmentedControl>
        {/* Plan 2026-10-15-005 — how many people a test covers; nothing while the ladder loads or in Real send. */}
        {sendMode === 'test' && tally !== null && testSendScopeSentence(tally.routedCount) !== null && (
          <Text size="xs" variant="tertiary">
            {testSendScopeSentence(tally.routedCount)}
          </Text>
        )}
        {/* U0g — the roster changed after the rep reviewed: say who and why
            (the server's sentence, names not ids) and give the one-click way out. */}
        {rosterChange !== null && (
          <div className="flex flex-col gap-2">
            <Alert status="warning">
              {rosterChange.reloaded
                ? `Recipients reloaded${tally ? ` — ${tally.routedCount} routed now` : ''}. Approve the groups again to send.`
                : rosterChange.message}
            </Alert>
            {!rosterChange.reloaded && (
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={isRechecking || actionsLocked}
                  onClick={() => void handleReloadAndReview()}
                >
                  <Icon type={ArrowClockwise} />
                  Reload and review again
                </Button>
              </div>
            )}
          </div>
        )}
        {/* Plan 2026-10-15-005 — a refused send's reason stays here until the next press. */}
        {lastSendFailure !== null && (
          <div className="flex flex-col gap-2" data-send-failure={lastSendFailure.status}>
            {lastSendFailure.status === 'error' ? (
              <Alert status="error" disableErrorLogging>
                <AlertHeader>{lastSendFailure.heading}</AlertHeader>
                <AlertDescription>{lastSendFailure.content}</AlertDescription>
              </Alert>
            ) : (
              <Alert status="info">
                <AlertHeader>{lastSendFailure.heading}</AlertHeader>
                <AlertDescription>{lastSendFailure.content}</AlertDescription>
              </Alert>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            variant="primary"
            size="lg"
            // The REAL routed subset (U3/R5), not the whole roster:
            // members claimed by a rung that has a sequence mapped,
            // first-match-wins. Members an unmapped rung claimed are
            // deliberately absent — that exclusion is the whole point
            // of Decision 13. The button stays disabled until the
            // ladder answers (`tally === null`), so `handleSend` never
            // sends an empty-by-accident list. Same gate for both
            // modes (U7b) — a test send is still gated on the rep's
            // own review, not on qualification (packet item 16).
            disabled={!gate.isSendEnabled || tally === null || actionsLocked || (sendMode === 'real' && isPlaySent(play))}
            onClick={() => void handleSend()}
          >
            <Icon type={PaperPlaneTilt} />
            {pendingAction === 'send'
              ? pendingSendMode === 'test'
                ? 'Sending test…'
                : 'Sending…'
              : sendMode === 'test'
                ? 'Send test'
                : 'Send enrollments'}
          </Button>
          {sendMode === 'real' && isPlaySent(play) && (
            <Text size="xs" variant="tertiary">
              {ALREADY_SENT_NOTE}
            </Text>
          )}
          {/* Plan 002, U6 (Decision 10) — hidden for a machine-created play
              (`machineCreatedAt` set). */}
          {!play?.machineCreatedAt && (
            <Button
              variant="secondary"
              size="lg"
              disabled={!gate.isSaveEnabled || actionsLocked}
              onClick={() => void handleApply()}
            >
              <Icon type={FloppyDisk} />
              {pendingAction === 'apply' ? 'Applying…' : 'Apply to campaigns'}
            </Button>
          )}
          {gate.unapprovedNote && (
            <Text size="xs" variant="tertiary">
              {gate.unapprovedNote}
            </Text>
          )}
        </div>
      </div>
        ) : (
          <div className="border-t border-border-secondary p-4">
            <Text size="sm" variant="tertiary">
              This Playbook saves as you edit. To send it, create a Play from a campaign on Home.
            </Text>
          </div>
        )}
        </CollapsibleCardBody>
      </CollapsibleCard>
      </div>

      {/* U6b (R9) — the dispatch-log inspector, mounted right below the
        Review and send card it reports on, as its own collapsed card. */}
      {playId && (
      <DispatchInspector
        playId={playId}
        members={members}
        focusSendId={focusSendId}
        focusTestDispatchId={focusTestDispatchId}
        lastRefusalNote={lastSendFailure?.note ?? null}
        // A name in "All people" opens that person's panel on the exact row,
        // under the group the ladder routes them to NOW ("Not in any group" when none).
        onOpenPerson={(memberId, focus) =>
          openMemberResult(
            playId,
            memberId,
            ladderQuery.data?.rungs.find((entry) => entry.matchedMemberIds.includes(memberId))?.rungId ?? null,
            focus,
          )
        }
      />
      )}
    </div>
  );
}
